import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useFolderQuery } from 'components/hooks/folderQueries';
import useForm from 'components/hooks/useForm';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ErrorMessage from 'components/application/ErrorMessage';
import ModalBox from 'components/application/ModalBox';
import TextField from 'components/application/TextField';

export default function RenameFolderModal({
  folderId,
  isConfirmDisabled = false,
  onClose,
  onConfirm,
  onInputFocus,
  requestError,
}) {
  const [currentFolder, setCurrentFolder, handleCurrentFolderInputChange] =
    useForm({ name: '' });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.RenameFolderModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: domainFolder } = useFolderQuery({ id: folderId });

  useEffect(() => {
    if (domainFolder?.data.attributes.name) {
      setCurrentFolder({
        ...currentFolder,
        name: domainFolder.data.attributes.name,
      });
    }
  }, [domainFolder?.data.attributes.name]);

  if (!domainFolder) return null;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              currentFolder.name.length === 0 ||
              currentFolder.name === domainFolder.data.attributes.name ||
              isConfirmDisabled
            }
            onClick={() => {
              onConfirm({
                folderParams: currentFolder,
                id: domainFolder.data.id,
              });
            }}
          >
            {i18nTCommon('shared.saveChanges')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={onClose}
    >
      <>
        <div className='modalbox-header tw-border-grey-100'>
          <h2 className='modalbox-header__title truncated-text-container tw-text-l tw-font-semibold tw-text-grey-900'>
            {i18nT('title', {
              interpolation: { escapeValue: false },
              name: domainFolder.data.attributes.name,
            })}
          </h2>
        </div>
        <div className='modalbox-body'>
          <ErrorMessage
            isFallback={false}
            modifiers='tw-mb-3'
            validationErrors={requestError.validationErrors}
          />
          <TextField
            autoFocus={true}
            fieldError={requestError.validationErrors.name}
            inputClassName='tw-px-3 tw-py-[9px]'
            isRequired={true}
            label={i18nTCommon('projects.folderName')}
            name='name'
            onChange={handleCurrentFolderInputChange}
            removeErrorStyling={onInputFocus}
            value={currentFolder.name}
          />
        </div>
      </>
    </ModalBox>
  );
}

RenameFolderModal.propTypes = {
  isConfirmDisabled: PropTypes.bool,
  folderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onInputFocus: PropTypes.func.isRequired,
  requestError: PropTypes.shape({
    isFallback: PropTypes.bool.isRequired,
    validationErrors: PropTypes.object.isRequired,
  }),
};
